import React, { useEffect } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import SimpleBar from 'simplebar-react';

import { Breaker } from 'types/Breaker';

import './BreakerModal.scss';

import placeholder from '../../assets/img/breaker-placeholder.png';
import { DropdownAnchor } from '../Dropdown/DropdownAnchor';
import { GalleryBreakerShareDropdown } from '../Sharing/ShareTargets';
import { ChevronIcon } from 'icons/chevron';
import { FLAGS, useFlags } from 'hooks/useFlags';
import { TraitIcon } from 'utils/allsvg';
import { IconButton } from 'components/Button/IconButton';
import { EyeIcon } from 'icons/eye';

type Props = {
  breaker: Breaker;
  onClose: () => void;
};

const BreakerModal = ({ breaker, onClose }: Props) => {
  const { id, name, imageUrls, attributes } = breaker;
  const { isFlagEnabled } = useFlags();
  useEffect(() => {
    document.addEventListener('keydown', onClose);

    return () => {
      document.removeEventListener('keydown', onClose);
    };
  });

  return (
    <div className="modal fade breaker-modal show" tabIndex={-1} id="selected-breaker-modal">
      <div className="modal-backdrop fade show" onClick={onClose}></div>

      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header is-img">
            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={onClose}
            />
            <LazyLoadImage
              className="breaker-image img-fluid"
              alt={name}
              src={imageUrls.full}
              placeholderSrc={placeholder}
              effect="blur"
            />
          </div>
          <div className="modal-body">
            {!isFlagEnabled(FLAGS.newGallery) ? (
              <>
                <div className="d-flex justify-content-between mb-3">
                  <h3 className="m-0">{name}</h3>
                  <div className="dropdown modal-dropdown d-flex gap-2">
                    <IconButton
                      icon={<EyeIcon />}
                      id="breaker-modal-dropdown"
                      data-bs-toggle="dropdown"
                    />

                    <div
                      className="dropdown-menu dropdown-menu-end dropdown-menu-dark"
                      aria-labelledby="breaker-modal-dropdown"
                    >
                      <a
                        className="dropdown-item"
                        target="_new"
                        href={`https://opensea.io/assets/0x0beed7099af7514ccedf642cfea435731176fb02/${id}`}
                      >
                        View on OpenSea
                      </a>

                      <a
                        className="dropdown-item"
                        target="_new"
                        href={`https://looksrare.org/collections/0x0beed7099af7514ccedf642cfea435731176fb02/${id}`}
                      >
                        View on LooksRare
                      </a>

                      <a
                        className="dropdown-item"
                        target="_new"
                        href={`https://nft.coinbase.com/nft/ethereum/0x0beed7099af7514ccedf642cfea435731176fb02/${id}`}
                      >
                        View on CoinbaseNFT (Beta)
                      </a>
                    </div>
                    <DropdownAnchor>
                      <GalleryBreakerShareDropdown breaker={breaker} />
                    </DropdownAnchor>
                  </div>
                </div>
                <SimpleBar
                  className="content-scrollbar content-scrollbar-old"
                  autoHide={false}
                  forceVisible={true}
                >
                  <div className="pe-3">
                    <div className="row g-3">
                      {attributes.map(({ traitType, value }) => {
                        return (
                          <div className="col-6" key={`${traitType}`}>
                            <div className="attribute h-100">
                              <div>
                                <TraitIcon traitType={traitType} />
                              </div>
                              <div>
                                <span className="trait">{`${traitType}`}</span>
                                <span className="value">{`${value}`}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </SimpleBar>
              </>
            ) : (
              <>
                <div className="details">
                  <p className="text-uppercase">Details</p>
                  <h3 className="m-0">{name}</h3>
                </div>
                <div className="modal-body-description">
                  <div className="dropdown modal-dropdown d-flex gap-2">
                    <button
                      id="breaker-modal-dropdown"
                      data-bs-toggle="dropdown"
                      className="btn btn-sm btn-sharing"
                    >
                      View On
                      <ChevronIcon />
                    </button>

                    <div
                      className="dropdown-menu dropdown-menu-end dropdown-menu-dark"
                      aria-labelledby="breaker-modal-dropdown"
                    >
                      <a
                        className="dropdown-item"
                        target="_new"
                        href={`https://opensea.io/assets/0x0beed7099af7514ccedf642cfea435731176fb02/${id}`}
                      >
                        View on OpenSea
                      </a>

                      <a
                        className="dropdown-item"
                        target="_new"
                        href={`https://looksrare.org/collections/0x0beed7099af7514ccedf642cfea435731176fb02/${id}`}
                      >
                        View on LooksRare
                      </a>

                      <a
                        className="dropdown-item"
                        target="_new"
                        href={`https://nft.coinbase.com/nft/ethereum/0x0beed7099af7514ccedf642cfea435731176fb02/${id}`}
                      >
                        View on CoinbaseNFT (Beta)
                      </a>
                    </div>
                    <DropdownAnchor>
                      <GalleryBreakerShareDropdown breaker={breaker} text="Share" />
                    </DropdownAnchor>
                  </div>
                  <p className="text-uppercase properties">properties</p>
                  <SimpleBar className="content-scrollbar" autoHide={false} forceVisible={true}>
                    <div>
                      <div className="trait-row g-3">
                        {attributes.map(({ traitType, value }) => {
                          return (
                            <div className="trait" key={`${traitType}`}>
                              <div className="attribute">
                                <div>
                                  <span className="trait">{`${traitType}`}</span>
                                  <span className="value">{`${value}`}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </SimpleBar>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreakerModal;
